import * as signalR from "@microsoft/signalr";
import { API_BASE_URL } from "@/lib/config";

export function getNotificationHubUrl(): string {
  const base = API_BASE_URL.replace(/\/+$/, "");
  const root = base.replace(/\/api$/i, "");
  return `${root}/notificationHub`;
}

function getAccessToken(): string {
  if (typeof document === "undefined") return "";
  const prefix = "auth-token=";
  const authTokenCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(prefix));
  if (!authTokenCookie) return "";
  const raw = authTokenCookie.slice(prefix.length);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

const FALLBACK_TRANSPORTS =
  signalR.HttpTransportType.ServerSentEvents |
  signalR.HttpTransportType.LongPolling;

const signalRLogger: signalR.ILogger = {
  log(level, message) {
    if (
      typeof message === "string" &&
      (message.includes("WebSocket") ||
        message.includes("stopped during negotiation"))
    ) {
      return;
    }
    if (level >= signalR.LogLevel.Error) {
      console.error(message);
    } else if (level >= signalR.LogLevel.Warning) {
      console.warn(message);
    }
  },
};

const STOP_DELAY_MS = 1000;

let sharedConnection: signalR.HubConnection | null = null;
let startPromise: Promise<void> | null = null;
let subscriberCount = 0;
let stopTimer: ReturnType<typeof setTimeout> | null = null;
const reconnectListeners = new Set<() => void>();

function buildConnection(): signalR.HubConnection {
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(getNotificationHubUrl(), {
      accessTokenFactory: getAccessToken,
      transport: FALLBACK_TRANSPORTS,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalRLogger)
    .build();
  connection.onreconnected(() => {
    reconnectListeners.forEach((listener) => listener());
  });
  return connection;
}

async function getNotificationConnection(): Promise<signalR.HubConnection | null> {
  if (typeof window === "undefined" || !getAccessToken()) return null;

  if (!sharedConnection) {
    sharedConnection = buildConnection();
    sharedConnection.onclose(() => {
      startPromise = null;
    });
  }

  const connection = sharedConnection;
  if (connection.state === signalR.HubConnectionState.Disconnected) {
    startPromise =
      startPromise ??
      connection
        .start()
        .catch(() => {
          if (sharedConnection === connection) sharedConnection = null;
        })
        .finally(() => {
          startPromise = null;
        });
  }
  if (startPromise) await startPromise;

  return sharedConnection;
}

export type NotificationHubHandlers = {
  onReceiveNotification: (payload: unknown) => void;
  onReconnected?: () => void;
};

export function subscribeNotificationHub(handlers: NotificationHubHandlers): () => void {
  subscriberCount += 1;
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  let connection: signalR.HubConnection | null = null;
  let disposed = false;
  const receive = (payload: unknown) => handlers.onReceiveNotification(payload);
  const reconnected = () => handlers.onReconnected?.();
  reconnectListeners.add(reconnected);

  void getNotificationConnection().then((conn) => {
    if (!conn || disposed) return;
    connection = conn;
    conn.on("ReceiveNotification", receive);
  });

  return () => {
    disposed = true;
    reconnectListeners.delete(reconnected);
    connection?.off("ReceiveNotification", receive);
    subscriberCount = Math.max(subscriberCount - 1, 0);
    if (subscriberCount > 0) return;

    stopTimer = setTimeout(() => {
      stopTimer = null;
      if (subscriberCount > 0 || !sharedConnection) return;
      const toStop = sharedConnection;
      sharedConnection = null;
      void toStop.stop().catch(() => {});
    }, STOP_DELAY_MS);
  };
}
